import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import {
  DelegateRequest,
  getDelegateList,
  getMaxDelegateRange,
  useDeleteDelegate,
  useRegisterDelegate,
} from 'api/delegateApi';
import { delegateKeys } from 'api/quires/queryKeys';
import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import { DELEGATE_TYPE, ROLE_TYPE } from 'types/enumType';
import { decryptId } from 'util/encryptUtil';
import { customStyles } from 'util/styleUtil';

import DelegateRegisterModal from './DelegateRegisterModal';
import DelegateTable from './DelegateTable';

type DelegateModalProps = {
  role: ROLE_TYPE | string;
  isOpen: boolean;
  refreshDelegateCount: () => void;
  onClose: () => void;
};

export default function DelegateModal({
  role,
  isOpen,
  refreshDelegateCount,
  onClose,
}: DelegateModalProps) {
  const { t } = useTranslation();

  const [isEditModal, setIsEditModal] = useState<boolean>(false);

  const {
    data: delegateInfo,
    refetch: refetchDelegate,
    isLoading: idDelegateLoading,
  } = useQuery({
    queryKey: delegateKeys.all,
    queryFn: () => getDelegateList(),
    enabled: false,
  });

  const { factoryId } = useRecoilValue(userInfoState);
  const { data: delegateDate } = useQuery({
    queryKey: delegateKeys.date(decryptId(factoryId)),
    queryFn: () => getMaxDelegateRange(decryptId(factoryId)),
  });

  useEffect(() => {
    if (isOpen) {
      refetchDelegate();
      refreshDelegateCount();
    }
  }, [isOpen]);

  const onOpenEditModal = () => {
    setIsEditModal(true);
  };
  const onCloseEditModal = () => {
    setIsEditModal(false);
  };

  const registerDelegateMutation = useRegisterDelegate();
  const onSubmitDelegate = (formData: DelegateRequest) => {
    if (
      formData.startDate &&
      formData.endDate &&
      differenceInCalendarDays(
        new Date(formData.startDate),
        new Date(formData.endDate),
      ) > 0
    ) {
      alert(t('conclusion.bigStartDate'));
      return;
    }
    if (
      differenceInCalendarDays(
        new Date(formData.endDate),
        new Date(formData.startDate),
      ) > (delegateDate?.maxDt ?? 1)
    ) {
      alert(t('validation.delegateMaxDate'));
      return;
    }

    registerDelegateMutation.mutate(formData, {
      onSuccess: (resp) => {
        if (resp.code === 200) {
          refetchDelegate();
          refreshDelegateCount();
          onCloseEditModal();
        } else {
          alert(resp.message);
        }
      },
    });
  };

  const deleteDelegateMutation = useDeleteDelegate();
  const onDeleteDelegate = (agentId: number) => {
    deleteDelegateMutation.mutate(agentId, {
      onSuccess: (resp) => {
        if (resp.code === 200) {
          refetchDelegate();
          refreshDelegateCount();
        } else {
          alert(resp.message);
        }
      },
    });
  };

  return (
    <>
      <ReactModal isOpen={isOpen} style={customStyles} onRequestClose={onClose}>
        <div id={'popup-wrap'} className={'delegate-modal'}>
          <div className={'popup-wrap'}>
            <div className={'popup-head'}>
              <p className={'poptit'}>{t('coop.delegateAndMandate')}</p>
            </div>
            <div className={'popup-cont'}>
              <div>
                <div className={'delegate-wrap'}>
                  <div className={'cont-wrap'}>
                    <div className={'box-wrap w100p'}>
                      <div className={'box'}>
                        <div className={'cont'}>
                          <p className={'subtit'}>{'위임 권한'}</p>
                          {(delegateInfo?.delegates?.length ?? 0) > 0 ? (
                            <div className={'tbl-wrap'}>
                              <DelegateTable
                                type={DELEGATE_TYPE.DELEGATE}
                                list={delegateInfo?.delegates ?? []}
                                onDelete={onDeleteDelegate}
                              />
                            </div>
                          ) : (
                            <div className={'center'}>
                              <p>{t('validation.noPermissionDele')}</p>
                              <p>{t('validation.permissionButton')}</p>
                              <button
                                type={'button'}
                                className={'orange_white_btn'}
                                onClick={onOpenEditModal}
                              >
                                {t('coop.permission-dele')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={'box'}>
                        <div className={'cont'}>
                          <p className={'subtit'}>{'수임 권한'}</p>
                          <div className={'tbl-wrap'}>
                            <DelegateTable
                              type={DELEGATE_TYPE.ACCEPT}
                              list={delegateInfo?.accepts ?? []}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={'btn-wrap'}>
                <button
                  type={'button'}
                  className={'gray_white_btn'}
                  onClick={onClose}
                >
                  {t('button.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </ReactModal>
      <DelegateRegisterModal
        isOpen={isEditModal}
        onSubmit={onSubmitDelegate}
        onClose={onCloseEditModal}
      />
    </>
  );
}
